import React from 'react';
import { useTranslation } from 'react-i18next';
import { BiBot, BiCheck, BiDollarCircle, BiImage, BiLink, BiPackage, BiTime, BiVideo } from 'react-icons/bi';

import { useAppSelector } from '@/app/hook';
import { CustomReactMarkdown } from '@/components/common/CustomReactMarkdown';
import TemplateCard, { TemplateCardData } from '@/components/common/TemplateCard';
import { PSButton } from '@/components/ui/button';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { selectCurrentDataState } from '../../create.slice';

interface Step4Props {
  goToStep: (step: number, section?: string) => void;
}

const Step4: React.FC<Step4Props> = ({ goToStep }) => {
  const { t } = useTranslation();
  const currentData = useAppSelector(selectCurrentDataState);
  const pkg = currentData.package;

  // Handle edit button clicks - navigate to specific steps
  const handleEdit = (step: number, section?: string) => {
    goToStep(step, section);
  };

  // Transform current data to TemplateCard format
  const transformToTemplateCardData = (data: any): TemplateCardData => ({
    name: data.name,
    type: data.type,
    price: data.price,
    version: data.version,
    configuration: data.configuration,
    data: data.data,
    publisher: data.package ? undefined : { name: '', icon: '' },
    downloadCount: 120,
  });

  let content;
  if (pkg) {
    // Package checkout view - simple display for purchased package
    const templateData = transformToTemplateCardData(pkg);
    content = (
      <div className="tw-max-w-4xl tw-mx-auto tw-p-6 tw-space-y-8 tw-font-['Lexend']">
        {/* Header */}
        <div className="tw-text-center tw-space-y-3">
          <div className="tw-flex tw-items-center tw-justify-center tw-gap-3">
            <div className="tw-w-12 tw-h-12 tw-bg-green-100 tw-rounded-full tw-flex tw-items-center tw-justify-center">
              <BiCheck className="tw-w-6 tw-h-6 tw-text-green-600" />
            </div>
            <h1 className="tw-text-3xl tw-font-bold tw-text-gray-900">{t('marketplace/step4/review/title')}</h1>
          </div>
          <p className="tw-text-lg tw-text-gray-600">{t('marketplace/step4/review/description')}</p>
        </div>

        {/* Package Preview with TemplateCard */}
        <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-6">
          <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-6 tw-text-center">{t('marketplace/step4/preview/title')}</h2>

          <div className="tw-flex tw-justify-center">
            <div className="tw-w-full tw-max-w-sm">
              <TemplateCard
                item={templateData}
                isSelected={false}
                isInEditMode={false}
                disableAction={true}
                onClick={() => undefined}
                className="tw-shadow-lg"
              />
            </div>
          </div>
        </div>

        {/* Final Actions */}
        <div className="tw-bg-gray-50 tw-p-6 tw-rounded-xl tw-border tw-border-gray-200">
          <div className="tw-text-center tw-space-y-3">
            <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900">{t('marketplace/step4/submit/title')}</h3>
            <p className="tw-text-sm tw-text-gray-600">{t('marketplace/step4/submit/description')}</p>
          </div>
        </div>
      </div>
    );
  } else {
    content = (
      <div className="tw-max-w-7xl tw-mx-auto tw-p-6 tw-space-y-8 tw-font-['Lexend']">
        {/* Header */}
        <div className="tw-text-center tw-space-y-3">
          <div className="tw-flex tw-items-center tw-justify-center tw-gap-3">
            <div className="tw-w-12 tw-h-12 tw-bg-green-100 tw-rounded-full tw-flex tw-items-center tw-justify-center">
              <BiCheck className="tw-w-6 tw-h-6 tw-text-green-600" />
            </div>
            <h1 className="tw-text-3xl tw-font-bold tw-text-gray-900">{t('marketplace/step4/review/title')}</h1>
          </div>
          <p className="tw-text-lg tw-text-gray-600">{t('marketplace/step4/review/description')}</p>
        </div>

        {/* Package Preview with TemplateCard */}
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-8">
          {/* Left Column - Package Details */}
          <div className="lg:tw-col-span-2  tw-space-y-8">
            <div className="tw-space-y-6">
              {/* Package Info Card */}
              <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-6">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <BiPackage className="tw-w-5 tw-h-5 tw-text-blue-600" />
                    <h2 className="tw-text-lg tw-font-semibold tw-text-gray-900">{t('marketplace/step4/packageInfo/title')}</h2>
                  </div>
                  <PSButton id="btn-step4-edit" variant="outline" size="sm" type="button" onClick={() => handleEdit(0)} className="tw-text-xs">
                    {t('marketplace/step4/buttons/edit')}
                  </PSButton>
                </div>

                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
                  <div>
                    <label className="tw-text-sm tw-font-medium tw-text-gray-500">{t('marketplace/step4/packageInfo/name')}</label>
                    <p className="tw-text-base tw-font-semibold tw-text-gray-900 tw-mt-1">{currentData.name}</p>
                  </div>
                  <div>
                    <label className="tw-text-sm tw-font-medium tw-text-gray-500">{t('marketplace/step4/packageInfo/version')}</label>
                    <p className="tw-text-base tw-font-semibold tw-text-gray-900 tw-mt-1">v{currentData.version}</p>
                  </div>
                  <div>
                    <label className="tw-text-sm tw-font-medium tw-text-gray-500">{t('marketplace/step4/packageInfo/type')}</label>
                    <p className="tw-text-base tw-font-semibold tw-text-gray-900 tw-mt-1 tw-capitalize">{currentData.type}</p>
                  </div>
                </div>
              </div>

              {/* Selected Chatbots Card */}
              <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-6">
                <div className="tw-flex tw-flex-col md:tw-flex-row tw-items-center tw-justify-between tw-mb-4">
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <BiBot className="tw-w-5 tw-h-5 tw-text-green-600" />
                    <h2 className="tw-text-lg tw-font-semibold tw-text-gray-900">{t('marketplace/step4/chatbots/title')}</h2>
                    <span className="tw-bg-blue-100 tw-text-blue-800 tw-text-xs tw-font-medium tw-px-2 tw-py-1 tw-rounded-full">
                      {currentData?.configuration?.bots?.length || 0} {t('marketplace/step4/chatbots/selected')}
                    </span>
                  </div>
                  <PSButton id="btn-step4-edit-1" variant="outline" size="sm" type="button" onClick={() => handleEdit(1)} className="tw-text-xs">
                    {t('marketplace/step4/buttons/edit')}
                  </PSButton>
                </div>

                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
                  {currentData?.configuration?.bots?.map((item: { bot_id: string; name: string }) => (
                    <div key={item.bot_id} className="tw-flex tw-items-center tw-gap-2 tw-p-2 tw-bg-gray-50 tw-rounded-lg">
                      <BiCheck className="tw-w-4 tw-h-4 tw-text-green-600 tw-flex-shrink-0" />
                      <span className="tw-text-sm tw-text-gray-700 tw-truncate">{item.name || item.bot_id}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Description Card */}
              <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-6">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                  <h2 className="tw-text-lg tw-font-semibold tw-text-gray-900">{t('marketplace/step4/description/title')}</h2>
                  <PSButton id="btn-step4-edit3" variant="outline" size="sm" type="button" onClick={() => handleEdit(2, 'description')} className="tw-text-xs">
                    {t('marketplace/step4/buttons/edit')}
                  </PSButton>
                </div>
                <div className="tw-prose tw-prose-sm tw-max-w-none">
                  {currentData?.data?.description ? (
                    <CustomReactMarkdown
                      content={currentData.data.description}
                      wrapperClassName="tw-py-3 tw-flex-col tw-justify-center tw-items-start tw-flex"
                    />
                  ) : (
                    <p className="tw-text-gray-500 tw-italic">{t('marketplace/step4/description/noDescription')}</p>
                  )}
                </div>
              </div>

              {/* Pricing Card */}
              <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-6">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <BiDollarCircle className="tw-w-5 tw-h-5 tw-text-yellow-600" />
                    <h2 className="tw-text-lg tw-font-semibold tw-text-gray-900">{t('marketplace/step4/pricing/title')}</h2>
                  </div>
                  <PSButton id="btn-step4-edit4" variant="outline" size="sm" type="button" onClick={() => handleEdit(2, 'pricing')} className="tw-text-xs">
                    {t('marketplace/step4/buttons/edit')}
                  </PSButton>
                </div>
                <div className="tw-text-3xl tw-font-bold tw-text-green-600">${currentData.price || '0'}</div>
              </div>

              {/* Media Card */}
              <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-6">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <BiImage className="tw-w-5 tw-h-5 tw-text-purple-600" />
                    <h2 className="tw-text-lg tw-font-semibold tw-text-gray-900">{t('marketplace/step4/media/title')}</h2>
                  </div>
                  <PSButton id="btn-step4-edit5" variant="outline" size="sm" type="button" onClick={() => handleEdit(2, 'media')} className="tw-text-xs">
                    {t('marketplace/step4/buttons/edit')}
                  </PSButton>
                </div>

                {currentData?.data?.image ? (
                  <div className="tw-space-y-3">
                    <img
                      src={currentData.data.image}
                      alt="Package preview"
                      className="tw-w-full tw-rounded-lg tw-object-cover tw-aspect-video tw-border tw-border-gray-200"
                    />
                    <p className="tw-text-xs tw-text-gray-500 tw-text-center">{t('marketplace/step4/media/packageImage')}</p>
                  </div>
                ) : (
                  <p className="tw-text-gray-500 tw-text-sm tw-italic">{t('marketplace/step4/media/noImage')}</p>
                )}

                {currentData.type === 'video' && currentData.data?.promotional_video && (
                  <div className="tw-mt-4 tw-pt-4 tw-border-t tw-border-gray-200">
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
                      <BiVideo className="tw-w-4 tw-h-4 tw-text-red-600" />
                      <span className="tw-text-sm tw-font-medium tw-text-gray-700">{t('marketplace/step4/media/promotionalVideo')}</span>
                    </div>
                    <div className="tw-max-w-xs tw-mx-auto">
                      <video
                        src={currentData.data.promotional_video}
                        controls
                        className="tw-w-full tw-rounded-lg tw-aspect-[9/16] tw-border-solid tw-border-2 tw-border-gray-300"
                      >
                        <track kind="captions" />
                      </video>
                    </div>
                  </div>
                )}
              </div>

              {/* Delivery Info (Video Packages Only) */}
              {currentData.type === 'video' && currentData.data?.delivery_delay && (
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-6">
                  <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <BiTime className="tw-w-5 tw-h-5 tw-text-orange-600" />
                      <h2 className="tw-text-lg tw-font-semibold tw-text-gray-900">{t('marketplace/step4/delivery/title')}</h2>
                    </div>
                    <PSButton id="btn-step4-edit6" variant="outline" size="sm" type="button" onClick={() => handleEdit(2, 'delivery')} className="tw-text-xs">
                      {t('marketplace/step4/buttons/edit')}
                    </PSButton>
                  </div>
                  <div className="tw-text-2xl tw-font-bold tw-text-orange-600">{currentData.data.delivery_delay}</div>
                  <p className="tw-text-sm tw-text-gray-500 tw-mt-1">{t('marketplace/step4/delivery/daysLabel')}</p>
                </div>
              )}

              {/* Links Card */}
              {(currentData?.data?.youtube_link || currentData?.data?.linkedin_link) && (
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-6">
                  <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                    <div className="tw-flex tw-items-center tw-gap-3">
                      <BiLink className="tw-w-5 tw-h-5 tw-text-indigo-600" />
                      <h2 className="tw-text-lg tw-font-semibold tw-text-gray-900">{t('marketplace/step4/links/title')}</h2>
                    </div>
                    <PSButton id="btn-step4-edit7" variant="outline" size="sm" type="button" onClick={() => handleEdit(2, 'links')} className="tw-text-xs">
                      {t('marketplace/step4/buttons/edit')}
                    </PSButton>
                  </div>
                  <div className="tw-space-y-3">
                    {currentData?.data?.youtube_link && (
                      <a
                        href={currentData.data.youtube_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tw-flex tw-items-center tw-gap-2 tw-text-blue-600 hover:tw-text-blue-800 tw-text-sm tw-transition-colors"
                      >
                        <span className="tw-font-medium">{t('marketplace/step4/links/youtube')}</span>
                        <span className="tw-text-xs tw-text-gray-500 tw-truncate">{currentData.data.youtube_link}</span>
                      </a>
                    )}
                    {currentData?.data?.linkedin_link && (
                      <a
                        href={currentData.data.linkedin_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tw-flex tw-items-center tw-gap-2 tw-text-blue-600 hover:tw-text-blue-800 tw-text-sm tw-transition-colors"
                      >
                        <span className="tw-font-medium">{t('marketplace/step4/links/linkedin')}</span>
                        <span className="tw-text-xs tw-text-gray-500 tw-truncate">{currentData.data.linkedin_link}</span>
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar - Sticky Preview */}
          <div className="lg:tw-col-span-1">
            <div className="tw-sticky tw-top-6">
              <div className="tw-bg-white tw-p-6 tw-rounded-xl tw-border tw-border-gray-200 tw-space-y-4 tw-shadow-lg">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <BiImage className="tw-w-5 tw-h-5 tw-text-indigo-600" />
                  <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900">{t('marketplace/step4/preview/title')}</h2>
                </div>
                <p className="tw-text-sm tw-text-gray-600">{t('marketplace/step4/preview/hint')}</p>
                <div className="tw-w-full">
                  <TemplateCard
                    item={transformToTemplateCardData(currentData)}
                    isSelected={false}
                    isInEditMode={false}
                    disableAction={true}
                    onClick={() => undefined}
                    className="tw-shadow-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Final Actions */}
        <div className="tw-bg-gray-50 tw-p-6 tw-rounded-xl tw-border tw-border-gray-200">
          <div className="tw-text-center tw-space-y-3">
            <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900">{t('marketplace/step4/submit/title')}</h3>
            <p className="tw-text-sm tw-text-gray-600">{t('marketplace/step4/submit/description')}</p>
          </div>
        </div>
      </div>
    );
  }
  return content;
};

export default WrappedErrorBoundary(Step4);
